import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { ContentHeader } from "@cx/ui";
import type { SalesRankingResult } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { todayInSaoPaulo } from "@cx/domain";
import { useSession } from "../../session";
import { ChartCard } from "./ChartCard";
import { count, money, monthYear, reportQuery } from "./labels";
import { statusList } from "./search";
import {
  FilterRow,
  ReportStatus,
  SettingsButton,
  StatusFilter,
  reportQueryOptions,
  useDashboardOptions,
} from "./shared";
import { CompanyTabs } from "./CompanyTabs";

const PATH = "/meu-negocio/vendedores-canais";
/** No gráfico, só as maiores séries (o resto fica na tabela). */
const CHART_ROWS = 5;

/** Vendedores e canais (RF-BI-09): 24 meses até o mês escolhido, ranking com "Outros". */
export function RankingPage(): ReactElement {
  const { me } = useSession();
  const search = useSearch({ from: PATH });
  const navigate = useNavigate({ from: PATH });
  const options = useDashboardOptions();
  const statuses = statusList(search.statuses);
  const set = (patch: Partial<typeof search>) =>
    navigate({ search: (s) => ({ ...s, ...patch }), replace: true });
  const sellers = can(me.permissions as Permissions, "sellers", "read");
  return (
    <>
      <ContentHeader
        title="Vendedores e canais"
        breadcrumb={["Meu Negócio", "Vendedores e canais"]}
        actions={<SettingsButton />}
      />
      <CompanyTabs />
      <FilterRow>
        <div className="col-6 col-md-2">
          <label className="form-label small" htmlFor="rk-month">
            Até o mês
          </label>
          <input
            id="rk-month"
            type="month"
            className="form-control"
            value={search.month ?? todayInSaoPaulo().slice(0, 7)}
            onChange={(e) => set({ month: e.target.value || undefined })}
          />
        </div>
        <div className="col-12 col-md-3">
          {options.data && (
            <StatusFilter
              statuses={options.data.statuses}
              defaults={options.data.settings.saleStatusIds}
              value={statuses}
              onChange={(ids) => set({ statuses: ids?.join(",") })}
            />
          )}
        </div>
      </FilterRow>
      {sellers && (
        <Ranking dimension="seller" title="Vendas por vendedor" month={search.month} statuses={statuses} />
      )}
      <Ranking dimension="channel" title="Vendas por canal" month={search.month} statuses={statuses} />
    </>
  );
}

function Ranking({
  dimension,
  title,
  month,
  statuses,
}: {
  dimension: "seller" | "channel";
  title: string;
  month: string | undefined;
  statuses: string[] | undefined;
}): ReactElement {
  const { companyId, api } = useSession();
  const q = useQuery({
    ...reportQueryOptions,
    queryKey: ["dashboard", companyId, "ranking", dimension, month, statuses?.join(",")],
    queryFn: () =>
      api<SalesRankingResult>(`/reports/sales-by-${dimension}${reportQuery({ month, statuses })}`),
  });
  const r = q.data;
  return (
    <div className="mb-4" data-testid={`ranking-${dimension}`}>
      <ReportStatus loading={q.isLoading} error={q.error} />
      {r && (
        <>
          <ChartCard
            title={`${title} (24 meses)`}
            categories={r.months.map(monthYear)}
            series={r.rows.slice(0, CHART_ROWS).map((row) => ({ name: row.label, data: row.series }))}
            unit="money"
            filename={`vendas-por-${dimension === "seller" ? "vendedor" : "canal"}`}
          />
          <div className="box card mt-3">
            {r.rows.length === 0 ? (
              <p className="text-muted p-3 mb-0">Sem vendas nos 24 meses.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0" aria-label={`${title}: ranking`}>
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">{dimension === "seller" ? "Vendedor" : "Canal"}</th>
                      <th scope="col" className="text-end">
                        Valor
                      </th>
                      <th scope="col" className="text-end">
                        Pedidos
                      </th>
                      <th scope="col" className="text-end">
                        {r.months.length ? monthYear(r.months.at(-1)!) : "Mês"}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.rows.map((row, i) => (
                      <tr key={row.key}>
                        <td className="text-muted">{row.key === "__outros__" ? "" : i + 1}</td>
                        <td>{row.label}</td>
                        <td className="text-end">{money(row.totalCents)}</td>
                        <td className="text-end">{count(row.orders)}</td>
                        <td className="text-end">{money(row.series.at(-1))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
